import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Shield } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";
import {
    STATUS_LABELS, STATUS_TONES, CATEGORY_LABELS, PRIORITY_LABELS, PRIORITY_TONES,
    formatTicketNumber,
} from "@/lib/supportHelpers";

const STATUS_TABS = [
    { value: "all", label: "All" },
    { value: "open", label: "Open" },
    { value: "awaiting_user", label: "Awaiting user" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];

const fmtRel = (iso) => {
    if (!iso) return "—";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
};

export default function AdminSupportPage() {
    const [data, setData] = useState(null);
    const [counts, setCounts] = useState({ open: 0, awaiting_admin: 0 });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [category, setCategory] = useState("all");
    const [priority, setPriority] = useState("all");
    const [sort, setSort] = useState("recent");
    useDocumentTitle("Admin · Support");

    useEffect(() => {
        // Pull counts once.
        api.get("/admin/overview").then((r) => {
            setCounts({
                open: r.data.support_tickets_open || 0,
                awaiting_admin: r.data.support_tickets_awaiting_admin || 0,
            });
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setData(null);
        const params = { page_size: 50, sort };
        if (search.trim()) params.search = search.trim();
        if (statusFilter !== "all") params.status = statusFilter;
        if (category !== "all") params.category = category;
        if (priority !== "all") params.priority = priority;
        api.get("/admin/support/tickets", { params })
            .then((r) => { if (!cancelled) setData(r.data); });
        return () => { cancelled = true; };
    }, [search, statusFilter, category, priority, sort]);

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="admin-support-page">
                <header className="space-y-1.5">
                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Shield className="h-3.5 w-3.5" />
                        Admin
                    </p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Support queue</h1>
                </header>

                <div className="flex flex-wrap gap-3">
                    <Card className="border-amber-200 bg-amber-50/50 shadow-sm" data-testid="admin-support-count-open">
                        <CardContent className="px-4 py-3">
                            <p className="text-xs uppercase tracking-wider text-amber-700">Open</p>
                            <p className="text-2xl font-semibold tabular-nums text-foreground">{counts.open}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-rose-200 bg-rose-50/50 shadow-sm" data-testid="admin-support-count-awaiting">
                        <CardContent className="px-4 py-3">
                            <p className="text-xs uppercase tracking-wider text-rose-700">Awaiting your reply</p>
                            <p className="text-2xl font-semibold tabular-nums text-foreground">{counts.awaiting_admin}</p>
                        </CardContent>
                    </Card>
                </div>

                <Card className="border-border/80 shadow-sm sticky top-16 z-10 backdrop-blur supports-[backdrop-filter]:bg-background/80" data-testid="admin-support-filters">
                    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center">
                        <div className="relative flex-1 min-w-0 sm:min-w-60">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search subject or email"
                                className="pl-9"
                                data-testid="admin-support-search"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="admin-support-status-chips">
                            {STATUS_TABS.map((t) => (
                                <button
                                    key={t.value}
                                    onClick={() => setStatusFilter(t.value)}
                                    data-testid={`admin-support-status-${t.value}`}
                                    className={cn(
                                        "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                                        statusFilter === t.value
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : "border-border bg-secondary text-foreground hover:bg-background"
                                    )}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="w-40" data-testid="admin-support-category-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All categories</SelectItem>
                                {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={priority} onValueChange={setPriority}>
                            <SelectTrigger className="w-36" data-testid="admin-support-priority-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All priorities</SelectItem>
                                {Object.entries(PRIORITY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger className="w-40" data-testid="admin-support-sort-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="recent">Most recent</SelectItem>
                                <SelectItem value="oldest">Oldest first</SelectItem>
                                <SelectItem value="priority_desc">Priority (high→low)</SelectItem>
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                <Card className="border-border/80 shadow-sm" data-testid="admin-support-table-card">
                    {!data ? (
                        <CardContent className="space-y-2 p-4">
                            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}
                        </CardContent>
                    ) : data.items.length === 0 ? (
                        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="admin-support-empty">
                            No tickets match these filters.
                        </CardContent>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-support-table">
                                <thead className="bg-secondary/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Ticket</th>
                                        <th className="px-4 py-3 text-left">Subject</th>
                                        <th className="px-4 py-3 text-left hidden md:table-cell">User</th>
                                        <th className="px-4 py-3 text-left hidden lg:table-cell">Category</th>
                                        <th className="px-4 py-3 text-left">Status</th>
                                        <th className="px-4 py-3 text-left hidden md:table-cell">Priority</th>
                                        <th className="px-4 py-3 text-left hidden sm:table-cell">Last reply</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border/70">
                                    {data.items.map((t) => (
                                        <tr
                                            key={t.id}
                                            className="hover:bg-secondary/40 cursor-pointer"
                                            onClick={() => { window.location.href = `/admin/support/${t.id}`; }}
                                            data-testid={`admin-support-row-${t.id}`}
                                        >
                                            <td className="px-4 py-3 font-mono text-xs tabular-nums">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {t.has_unread_for_admin && <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" data-testid={`admin-support-row-unread-${t.id}`} />}
                                                    {formatTicketNumber(t.ticket_number)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Link to={`/admin/support/${t.id}`} className="font-medium text-foreground hover:text-primary" onClick={(e) => e.stopPropagation()}>
                                                    {t.subject}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{t.user_email}</td>
                                            <td className="px-4 py-3 hidden lg:table-cell">
                                                <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs">
                                                    {CATEGORY_LABELS[t.category] || t.category}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", STATUS_TONES[t.status])}>
                                                    {STATUS_LABELS[t.status]}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                <span className={cn("rounded-full border px-2 py-0.5 text-xs", PRIORITY_TONES[t.priority])}>
                                                    {PRIORITY_LABELS[t.priority]}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                                                {fmtRel(t.last_message_at)} · by {t.last_message_by === "admin" ? "you" : "user"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>
        </Layout>
    );
}
